import { z } from 'zod'
import { SolarActuator, SolarActuatorForm } from '../../models/smart-home.model'
import { useCustomTranslation } from '@e3dc-react/shell/Hooks/useCustomTranslation'
import { FormInitializerReturnTypeZod, ValidationMessageKeys } from 'src/Configuration/validation'
import { HOURS_FORMAT_PATTERN, HOURS_VALID_TIME_REGEX } from '../../consts'
import { Days } from '@e3dc-react/shell/Libs/date/types'

const MAX_USED_POWER = 19999

export const useSolarActuatorFormInitializer = (actuator: SolarActuator): FormInitializerReturnTypeZod<SolarActuatorForm, SolarActuatorForm> => {
  const { t } = useCustomTranslation()

  const initialValues: SolarActuatorForm = {
    usedPower: Number.parseInt(actuator.descriptions.usedPower) ?? 350,
    minOn: actuator.descriptions.minOn ?? '00:30',
    minOff: actuator.descriptions.minOff ?? '05:30',
    week: (actuator.descriptions.week as Days) ?? {
      mo: true,
      tu: true,
      we: true,
      th: true,
      fr: true,
      sa: true,
      su: true,
    },
    auto: actuator.descriptions.auto ?? true,
  }

  const validationSchema = z.object({
    usedPower: z.preprocess(
      value => {
        if (typeof value === 'string' && value.trim() === '') {
          return undefined
        }

        return Number(value)
      },
      z
        .number({ required_error: t(ValidationMessageKeys.required), invalid_type_error: t(ValidationMessageKeys.number) })
        .int({ message: t(ValidationMessageKeys.integer) })
        .min(0, { message: t(ValidationMessageKeys.min, { value: 0 }) })
        .max(MAX_USED_POWER, { message: t(ValidationMessageKeys.max, { value: MAX_USED_POWER }) })
    ),
    minOn: z
      .string()
      .transform(value =>
        value
          .replace(':', '')
          .slice(0, 4)
          .replace(/^(\d{2})(\d{0,2})/, '$1:$2')
      )
      .refine(value => value, {
        message: t(ValidationMessageKeys.required),
      })
      .refine(value => HOURS_FORMAT_PATTERN.exec(value) !== null, {
        message: t('smart-home.solar.invalid-time'),
      })
      .refine(value => HOURS_VALID_TIME_REGEX.exec(value) !== null, {
        message: t(ValidationMessageKeys.hoursFormat),
      }),
    minOff: z
      .string()
      .transform(value =>
        value
          .replace(':', '')
          .slice(0, 4)
          .replace(/^(\d{2})(\d{0,2})/, '$1:$2')
      )
      .refine(value => value, {
        message: t(ValidationMessageKeys.required),
      })
      .refine(value => HOURS_FORMAT_PATTERN.exec(value) !== null, {
        message: t(ValidationMessageKeys.hoursFormat),
      })
      .refine(value => HOURS_VALID_TIME_REGEX.exec(value) !== null, {
        message: t(ValidationMessageKeys.hoursFormat),
      }),
    week: z.object({
      mo: z.boolean(),
      tu: z.boolean(),
      we: z.boolean(),
      th: z.boolean(),
      fr: z.boolean(),
      sa: z.boolean(),
      su: z.boolean(),
    }),
    auto: z.boolean(),
  })

  return { initialValues, validationSchema } as FormInitializerReturnTypeZod<SolarActuatorForm, SolarActuatorForm>
}
